/**
 * Ticket Module Angular Animations
 *
 * Animation definitions for ticket drawer components, matching the prototype's
 * Framer Motion animations with precise timing and easing.
 *
 * Design System Reference: ticket module design system
 * Prototype Reference: Ticket drawer component specifications
 */

import {
  trigger,
  state,
  style,
  transition,
  animate,
  AnimationTriggerMetadata
} from '@angular/animations';

/**
 * Sidebar collapse/expand animation
 *
 * Matches prototype spec:
 * - Width: 256px ↔ 0px
 * - Opacity: 1 ↔ 0
 * - Duration: 200ms
 * - Easing: ease-out
 *
 * Usage in template:
 * <div [@sidebarCollapse]="isCollapsed ? 'collapsed' : 'expanded'">
 */
export const sidebarCollapseAnimation: AnimationTriggerMetadata = trigger('sidebarCollapse', [
  // Expanded state - sidebar visible at full width
  state('expanded', style({
    width: '256px',
    opacity: 1,
    overflow: 'hidden'
  })),

  // Collapsed state - sidebar hidden
  state('collapsed', style({
    width: '0px',
    opacity: 0,
    overflow: 'hidden'
  })),

  // Transition: expanded → collapsed (collapse animation)
  transition('expanded => collapsed', [
    animate('200ms ease-in')
  ]),

  // Transition: collapsed → expanded (expand animation)
  transition('collapsed => expanded', [
    animate('200ms ease-out')
  ])
]);

/**
 * Fade in/out animation
 *
 * Used for content that should fade in/out without width animation
 * Duration: 200ms
 *
 * Usage in template:
 * <div [@fadeInOut]>
 */
export const fadeInOutAnimation: AnimationTriggerMetadata = trigger('fadeInOut', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('200ms ease-out', style({ opacity: 1 }))
  ]),
  transition(':leave', [
    animate('200ms ease-in', style({ opacity: 0 }))
  ])
]);

/**
 * Slide in/out animation (from left)
 *
 * Used for sidebar content that slides in from the left
 * Duration: 200ms
 *
 * Usage in template:
 * <div [@slideInOut]>
 */
export const slideInOutAnimation: AnimationTriggerMetadata = trigger('slideInOut', [
  transition(':enter', [
    style({
      transform: 'translateX(-100%)',
      opacity: 0
    }),
    animate('200ms ease-out', style({
      transform: 'translateX(0)',
      opacity: 1
    }))
  ]),
  transition(':leave', [
    animate('200ms ease-in', style({
      transform: 'translateX(-100%)',
      opacity: 0
    }))
  ])
]);

/**
 * Scale hover animation state
 *
 * Used for interactive elements that scale on hover
 *
 * Usage in template:
 * <button [@scaleHover]="isHovered ? 'hovered' : 'normal'">
 */
export const scaleHoverAnimation: AnimationTriggerMetadata = trigger('scaleHover', [
  state('normal', style({
    transform: 'scale(1)'
  })),
  state('hovered', style({
    transform: 'scale(1.05)'
  })),
  transition('normal <=> hovered', [
    animate('150ms ease-out')
  ])
]);

/**
 * Collapse toggle button animation
 *
 * Used for the collapsed sidebar toggle button that expands on hover
 * Width: 32px → 40px on hover
 *
 * Usage in template:
 * <button [@toggleButtonHover]="isHovered ? 'hovered' : 'normal'">
 */
export const toggleButtonHoverAnimation: AnimationTriggerMetadata = trigger('toggleButtonHover', [
  state('normal', style({
    width: '32px'
  })),
  state('hovered', style({
    width: '40px'
  })),
  transition('normal <=> hovered', [
    animate('150ms ease-out')
  ])
]);

/**
 * Stagger animation for list items
 *
 * Used to stagger the appearance of list items
 *
 * Usage in template:
 * <div *ngFor="let item of items" [@staggerItem]>
 */
export const staggerItemAnimation: AnimationTriggerMetadata = trigger('staggerItem', [
  transition(':enter', [
    style({
      opacity: 0,
      transform: 'translateY(10px)'
    }),
    animate('200ms ease-out', style({
      opacity: 1,
      transform: 'translateY(0)'
    }))
  ])
]);

/**
 * Slide down animation for search bar
 *
 * Used for the header search bar that slides down when opened
 * Duration: 200ms
 *
 * Usage in template:
 * <div [@slideDown]>
 */
export const slideDownAnimation: AnimationTriggerMetadata = trigger('slideDown', [
  transition(':enter', [
    style({
      opacity: 0,
      height: 0,
      overflow: 'hidden'
    }),
    animate('200ms ease-out', style({
      opacity: 1,
      height: '*'
    }))
  ]),
  transition(':leave', [
    animate('200ms ease-in', style({
      opacity: 0,
      height: 0,
      overflow: 'hidden'
    }))
  ])
]);

/**
 * Tab content transition animation
 *
 * Matches prototype spec:
 * - Initial: opacity 0, translateY(10px)
 * - Animate: opacity 1, translateY(0)
 * - Exit: opacity 0, translateY(-10px)
 * - Duration: 200ms
 *
 * Usage in template:
 * <div [@tabContentTransition]>
 */
export const tabContentTransitionAnimation: AnimationTriggerMetadata = trigger('tabContentTransition', [
  transition(':enter', [
    style({
      opacity: 0,
      transform: 'translateY(10px)'
    }),
    animate('200ms ease-out', style({
      opacity: 1,
      transform: 'translateY(0)'
    }))
  ]),
  transition(':leave', [
    animate('200ms ease-in', style({
      opacity: 0,
      transform: 'translateY(-10px)'
    }))
  ])
]);

/**
 * Export all animations as a single array for easy component registration
 *
 * Usage in @Component decorator:
 * @Component({
 *   animations: ticketAnimations
 * })
 */
export const ticketAnimations: AnimationTriggerMetadata[] = [
  sidebarCollapseAnimation,
  fadeInOutAnimation,
  slideInOutAnimation,
  scaleHoverAnimation,
  toggleButtonHoverAnimation,
  staggerItemAnimation,
  slideDownAnimation,
  tabContentTransitionAnimation
];
